import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../utils/supabase';
import { UtCard, type UtCardPlayer } from '../UtCard';

/** Centred auth panel used by register / forgot / reset / verify screens. */
export const UtAuthFrame: React.FC<{ title: string; sub?: React.ReactNode; children: React.ReactNode; wide?: boolean }> = ({ title, sub, children, wide }) => (
  <section className={`mx-auto flex w-full flex-col gap-4 py-6 ${wide ? 'max-w-[560px]' : 'max-w-[420px]'}`}>
    <div className="flex flex-col gap-1">
      <h1 className="font-ut-display text-[38px] font-extrabold uppercase leading-none">{title}</h1>
      {sub && <span className="text-sm text-ut-mut">{sub}</span>}
    </div>
    {children}
  </section>
);

/** Public links shown under the sign-in form. */
export const UtPublicLinks: React.FC = () => (
  <div className="flex flex-wrap items-center gap-2.5 border-t border-ut-line pt-2.5 text-[13px] text-ut-mut">
    <Link to="/leaderboards" className="font-semibold text-ut-ink hover:text-ut-acc">
      Browse leaderboards
    </Link>
    ·
    <Link to="/results" className="font-semibold text-ut-ink hover:text-ut-acc">
      Results
    </Link>
    ·
    <Link to="/players" className="font-semibold text-ut-ink hover:text-ut-acc">
      Players
    </Link>
  </div>
);

/** The three highest-XP players, for the fanned cards on the sign-in screen. */
export function useTopXpCards(n = 3): UtCardPlayer[] {
  const [cards, setCards] = useState<UtCardPlayer[]>([]);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('player_xp')
      .select('xp, rarity, player:players!inner ( id, friendly_name )')
      .order('xp', { ascending: false })
      .limit(n)
      .then(({ data }) => {
        if (cancelled || !data) return;
        setCards(
          (data as unknown as { xp: number; rarity: string | null; player: { id: string; friendly_name: string } | { id: string; friendly_name: string }[] }[]).map(
            (r) => {
              const p = Array.isArray(r.player) ? r.player[0] : r.player;
              return { id: p.id, name: p.friendly_name, xp: r.xp, rarity: r.rarity };
            },
          ),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [n]);
  return cards;
}

/** 2nd / 1st / 3rd fanned at −12° / +12°, the middle card lifted and scaled. */
export const CardFan: React.FC<{ cards: UtCardPlayer[] }> = ({ cards }) => {
  if (cards.length < 3) return <div className="min-h-[280px]" />;
  const order = [cards[1], cards[0], cards[2]];
  const transforms = ['rotate(-12deg) translateY(14px)', 'translateY(-10px) scale(1.08)', 'rotate(12deg) translateY(14px)'];
  return (
    <div className="flex min-h-[280px] items-center justify-center py-6">
      {order.map((c, i) => (
        <UtCard key={c.id} player={c} size="fan" to={null} className="-mx-[34px]" style={{ transform: transforms[i], zIndex: i === 1 ? 2 : 1 }} />
      ))}
    </div>
  );
};
